/**
 * The area of the app each tRPC procedure belongs to (ADR 0016): what a visit read and changed,
 * calls per area, and the procedures nobody called. The admin interface's procedures are keyed
 * `admin.<path>`. `routers/procedure-areas.test.ts` in packages/api fails on a procedure without
 * an area, and on an area entry whose procedure is gone.
 */

export const PROCEDURE_AREAS_LIST = [
  "overview",
  "parts",
  "vehicles",
  "work_orders",
  "templates",
  "orders",
  "to_order",
  "suppliers",
  "locations",
  "tags",
  "chassis_codes",
  "attachments",
  "search",
  "settings",
  "data",
  "garages",
  "members",
  "account",
  "demo",
  "system",
  "admin",
] as const;
export type ProcedureArea = (typeof PROCEDURE_AREAS_LIST)[number];

/** Every procedure in `prefix.*` belongs to `area`. */
const all = (area: ProcedureArea, prefix: string, names: readonly string[]) =>
  Object.fromEntries(names.map((name) => [`${prefix}.${name}`, area]));

export const PROCEDURE_AREAS: Readonly<Record<string, ProcedureArea>> = {
  overview: "overview",
  search: "search",
  "system.info": "system",
  ...all("account", "account", [
    "changePassword",
    "deleteMyAccount",
    "finishWelcome",
    "me",
    "removeAvatar",
    "requestPasswordReset",
    "resetPassword",
    "setPin",
    "setShareUsage",
    "signIn",
    "signOut",
    "updatePreferences",
    "updateProfile",
  ]),
  ...all("account", "localUsers", [
    "add",
    "delete",
    "list",
    "recoverPin",
    "removePin",
    "setAdmin",
    "signIn",
    "switchOff",
    "switchOn",
  ]),
  ...all("attachments", "attachments", ["delete", "makeFirst", "update"]),
  ...all("chassis_codes", "chassisCodes", ["addMany", "create", "delete", "list", "update"]),
  "data.loadDemo": "data",
  ...all("data", "installation", ["backupNow", "backups", "info", "updateBackupSettings"]),
  ...all("garages", "garages", ["access", "create", "list", "rename"]),
  ...all("members", "invitations", ["accept", "get"]),
  ...all("members", "members", [
    "changeRole",
    "deactivate",
    "invite",
    "list",
    "reactivate",
    "remove",
    "resend",
    "revoke",
  ]),
  ...all("parts", "links", ["create", "delete", "update"]),
  ...all("locations", "locations", ["create", "delete", "list", "update"]),
  ...all("orders", "orders", [
    "addLine",
    "cancel",
    "create",
    "delete",
    "get",
    "list",
    "place",
    "quickPurchase",
    "receive",
    "receiveAll",
    "removeLine",
    "revertToDraft",
    "undoReceipt",
    "update",
    "updateLine",
  ]),
  ...all("to_order", "orders", ["addToDraft", "partsList", "toOrder"]),
  ...all("parts", "parts", [
    "adjust",
    "create",
    "createSource",
    "delete",
    "deleteSource",
    "findByNumbers",
    "get",
    "link",
    "list",
    "movements",
    "setArchived",
    "setStock",
    "undoMovement",
    "unlink",
    "update",
    "updateSource",
  ]),
  ...all("demo", "publicDemo", ["end", "request", "reset", "start"]),
  ...all("settings", "settings", ["get", "update"]),
  ...all("suppliers", "suppliers", ["create", "delete", "list", "update"]),
  ...all("tags", "tags", ["addMainGroups", "create", "delete", "list", "update"]),
  ...all("templates", "templates", [
    "addPart",
    "create",
    "createWorkOrder",
    "delete",
    "get",
    "list",
    "removePart",
    "update",
    "updatePart",
  ]),
  ...all("vehicles", "vehicles", ["create", "delete", "get", "list", "setArchived", "update"]),
  ...all("work_orders", "workOrders", [
    "addRequirement",
    "complete",
    "create",
    "delete",
    "get",
    "list",
    "removeRequirement",
    "reopen",
    "saveAsTemplate",
    "setStatus",
    "update",
    "updateRequirement",
    "useLinkedPart",
  ]),
  ...all("admin", "admin.garages", ["create", "get", "list", "rename", "setStatsExcluded"]),
  ...all("admin", "admin.settings", ["get", "update"]),
  ...all("admin", "admin.users", [
    "delete",
    "get",
    "invitations",
    "list",
    "setAdmin",
    "setDisabled",
  ]),
};

/** A procedure's area; null for one the map doesn't know (a newer installation's). */
export function procedureArea(path: string): ProcedureArea | null {
  return Object.hasOwn(PROCEDURE_AREAS, path) ? (PROCEDURE_AREAS[path] ?? null) : null;
}
